import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { COST_GROUPS, RUN_STATUSES, listAgents } from '@mkcore/agents/schema';
import { Button, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime, formatMicros, formatNumber } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { formatDuration } from '@/features/admin/job-status';
import { BudgetCard, RunStatusBadge, costsQuery, runsQuery, tokenCount } from '@/features/agents/ai';
import { AgentBudgets } from '@/features/agents/budgets';
import { AgentWorkers } from '@/features/agents/workers';

const search = z.object({
  status: z.enum(RUN_STATUSES).optional(),
  agentId: z.string().optional(),
  task: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/agents/runs/')({ validateSearch: search, component: Runs });

/**
 * Agent runs (CO-0970, CO-0971): every run of an agent or AI job with its status, tokens and costs, filterable on
 * failed runs, agent and task; the costs of this month per agent, task or model; and the monthly budget (CO-0972).
 */
function Runs() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(runsQuery({ status: s.status, agentId: s.agentId || undefined, task: s.task || undefined }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const agents = useQuery({ queryKey: ['agent', 'agent'], queryFn: () => call(listAgents, {}) });
  const [groupBy, setGroupBy] = useState<(typeof COST_GROUPS)[number]>('agent');
  const costs = useQuery(costsQuery(groupBy));
  return (
    <>
      <Link to="/agents" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.title')}
      </Link>
      <PageHeader eyebrow={t('nav.agents')} title={t('agents.runs.title')} description={t('agents.runs.description')} />
      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <BudgetCard runsLink={false} />
        <section aria-label={t('agents.costs.title')} className="grid content-start gap-2">
          <div className="flex items-end gap-2">
            <h2 className="flex-1 text-lg font-bold">{t('agents.costs.title')}</h2>
            <Field label={t('agents.costs.groupBy')} className="w-44">
              {(p) => (
                <NativeSelect {...p} value={groupBy} onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}>
                  {COST_GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {t(`agents.costs.groups.${g}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          {!costs.data ? (
            <Skeleton className="h-24" />
          ) : !costs.data.items.length ? (
            <p className="text-sm text-fg-3">{t('agents.costs.none')}</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t(`agents.costs.groups.${groupBy}`)}</Th>
                  <Th className="text-right">{t('agents.runs.calls')}</Th>
                  <Th className="hidden sm:table-cell text-right">{t('agents.runs.tokens')}</Th>
                  <Th className="text-right">{t('agents.runs.cost')}</Th>
                </Tr>
              </THead>
              <TBody>
                {[...costs.data.items, costs.data.total].map((c, i) => (
                  <Tr key={`${c.key}-${i}`} className={i === costs.data!.items.length ? 'font-bold' : undefined}>
                    <Td className="max-w-56 truncate">{c.label}</Td>
                    <Td className="text-right tabular">{formatNumber(c.calls)}</Td>
                    <Td className="hidden sm:table-cell text-right tabular text-fg-2">{tokenCount(c.inputTokens, c.outputTokens)}</Td>
                    <Td className="text-right tabular">{formatMicros(c.costMicros, c.currency)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </section>
      </div>
      <div className="mb-6">
        <AgentBudgets />
      </div>
      <div className="mb-6">
        <AgentWorkers />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.status')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Search['status'] })}>
              <option value="">{t('common.all')}</option>
              {RUN_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`agents.runs.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('agents.runs.agent')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.agentId ?? ''} onChange={(e) => set({ agentId: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {(agents.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('agents.runs.task')} hint={t('agents.runs.taskHint')} className="w-56">
          {(p) => <Input {...p} defaultValue={s.task ?? ''} onBlur={(e) => set({ task: e.target.value.trim() || undefined })} />}
        </Field>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('agents.runs.empty')} description={t('agents.runs.emptyHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('agents.runs.task')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('agents.runs.trigger')}</Th>
              <Th className="hidden md:table-cell text-right">{t('agents.runs.tokens')}</Th>
              <Th className="text-right">{t('agents.runs.cost')}</Th>
              <Th className="hidden lg:table-cell text-right">{t('agents.runs.duration')}</Th>
              <Th>{t('agents.runs.started')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Link to="/agents/runs/$id" params={{ id: r.id }} className="font-bold hover:underline">
                    {r.task}
                  </Link>
                  <div className="text-xs text-fg-3">{r.agentName ?? t('agents.runs.noAgent')}</div>
                </Td>
                <Td>
                  <RunStatusBadge status={r.status} />
                  {r.error && <div className="mt-1 max-w-xs truncate text-xs text-danger">{r.error}</div>}
                </Td>
                <Td className="hidden md:table-cell text-fg-2">{t(`agents.runs.triggers.${r.trigger}`)}</Td>
                <Td className="hidden md:table-cell text-right tabular text-fg-2">{tokenCount(r.inputTokens, r.outputTokens)}</Td>
                <Td className="text-right tabular">{formatMicros(r.costMicros, r.currency)}</Td>
                <Td className="hidden lg:table-cell text-right tabular text-fg-2">{r.durationMs === null ? '—' : formatDuration(r.durationMs)}</Td>
                <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(r.startedAt)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}
